import { useTranslation } from "react-i18next";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { useApprovalIntegrity, useApprovalMutation } from "../../lib/api";

const SEVERITY_PILL: Record<string, string> = {
  Red: "bg-red-50 text-status-red", Amber: "bg-amber-50 text-status-amber",
};

const AREA_KEYS = ["leave", "expense", "contractor_time"] as const;
const FINDING_KEYS = ["self_approved", "approver_is_creator", "no_approver"] as const;

/** Approval integrity (HRX-01, 9.7): whether an approval on this deployment means anything.
 *
 * The audit found no workflows and both self-approval guards off, so nothing stopped somebody approving their
 * own request. The screen shows what is enforced, what slipped through before it was, and — the part easiest to
 * get wrong — when a control is configured but bypassed. */
export default function ApprovalIntegrity() {
  const { t } = useTranslation();
  const q = useApprovalIntegrity();
  const save = useApprovalMutation();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-32" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-6" data-admin-approvals>
      <p className="max-w-3xl text-sm text-brand-grey">{t("admin.appr.hint")}</p>

      <Card className="overflow-hidden" data-admin-appr-guards>
        <div className="border-b border-brand-grey-light px-4 py-3">
          <h3 className="font-heading text-base font-semibold">{t("admin.appr.controls")}</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
            <tr>
              <th className="px-4 py-2">{t("admin.appr.control")}</th>
              <th className="px-4 py-2">{t("admin.appr.enforcedBy")}</th>
              <th className="px-2 py-2 text-center">{t("admin.appr.on")}</th>
            </tr>
          </thead>
          <tbody>
            {d.guards.map((g) => (
              <tr key={g.key} className="border-t border-brand-grey-light" data-admin-appr-guard={g.key}>
                <td className="px-4 py-2">
                  <div className="font-medium">{g.label}</div>
                  {g.bypassed_by_workflow && (
                    <div className="text-[11px] font-semibold text-status-red">{t("admin.appr.bypassed")}</div>
                  )}
                  {!g.configured && <div className="text-[11px] text-status-amber">{t("admin.appr.notConfigured")}</div>}
                </td>
                <td className="px-4 py-2 text-[11px] text-brand-grey">{g.control}</td>
                <td className="px-2 py-2 text-center">
                  <input type="checkbox" checked={g.configured} disabled={save.isPending || !d.can_manage}
                    data-admin-appr-toggle={g.key}
                    onChange={(e) => save.mutate({ key: g.key, on: e.target.checked ? 1 : 0 })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {d.workflows.length === 0 && (
          <p className="border-t border-brand-grey-light px-4 py-2 text-[11px] text-brand-grey">{t("admin.appr.noWorkflows")}</p>
        )}
      </Card>

      <Card className="overflow-hidden" data-admin-appr-findings>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
          <h3 className="font-heading text-base font-semibold">{t("admin.appr.record")}</h3>
          <span className={`text-xs font-semibold ${d.total_findings ? "text-status-amber" : "text-status-green"}`}>
            {t("admin.appr.findings", { n: d.total_findings })}
          </span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
            <tr>
              <th className="px-4 py-2">{t("admin.appr.area")}</th>
              <th className="px-2 py-2 text-right">{t("admin.appr.checked")}</th>
              <th className="px-2 py-2 text-right">{t("admin.appr.sound")}</th>
              {FINDING_KEYS.map((k) => <th key={k} className="px-2 py-2 text-right">{t(`admin.appr.finding.${k}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {AREA_KEYS.map((key) => {
              const a = d.areas[key];
              if (!a) return null;
              return (
                <tr key={key} className="border-t border-brand-grey-light" data-admin-appr-area={key}>
                  <td className="px-4 py-2">{t(`admin.appr.areas.${key}`)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{a.total}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-status-green">{a.sound}</td>
                  {FINDING_KEYS.map((k) => (
                    <td key={k} className="px-2 py-2 text-right tabular-nums">
                      {a.counts[k]
                        ? <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${SEVERITY_PILL[k === "self_approved" ? "Red" : "Amber"]}`}>{a.counts[k]}</span>
                        : <span className="text-brand-grey">–</span>}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="border-t border-brand-grey-light px-4 py-2 text-[11px] text-brand-grey">{t("admin.appr.recordHint")}</p>
      </Card>

      {d.unread_hrms_feed.length > 0 && (
        <Card className="p-4" data-admin-appr-feed>
          <h3 className="font-heading text-base font-semibold">{t("admin.appr.feed.title")}</h3>
          <p className="mt-1 text-[12px] text-brand-grey">{t("admin.appr.feed.hint")}</p>
          <ul className="mt-2 text-sm">
            {d.unread_hrms_feed.map((r) => (
              <li key={r.doctype} className="flex justify-between border-t border-brand-grey-light/70 py-1">
                <span>{r.doctype}</span><span className="tabular-nums text-brand-grey">{r.n}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {save.isError && <ErrorBox error={save.error} />}
    </div>
  );
}
